import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";
import {
  formatCompactCurrency,
  formatCompactNumber,
  formatPercentage,
} from "../../../utils/formatters";
import { CampaignAdData } from "../types";

interface MetricsByCampaignAdGroupAdTableProps {
  data: CampaignAdData;
  titleKey?: string;
  showExport?: boolean;
  maxHeight?: string;
}

export default function MetricsByCampaignAdGroupAdTable({
  data,
  titleKey = "reports.metricsByCampaignAdGroupAd",
  showExport = true,
  maxHeight = "65vh",
}: MetricsByCampaignAdGroupAdTableProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(
    new Set(),
  );
  const [expandedAdGroups, setExpandedAdGroups] = useState<Set<string>>(
    new Set(),
  );

  const { campaigns, totals } = data;

  const getAdGroupKey = (campaignId: string, adGroupId: string) =>
    `${campaignId}::${adGroupId}`;

  const toggleCampaign = (campaignId: string) => {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  };

  const toggleAdGroup = (campaignId: string, adGroupId: string) => {
    const key = getAdGroupKey(campaignId, adGroupId);
    setExpandedAdGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const exportToCSV = () => {
    const headers = [
      "Type",
      "Name",
      "Clicks",
      "Conversions",
      "CVR (%)",
      "Revenue",
      "Revenue MoM (%)",
      "Revenue YoY (%)",
    ];

    const rows: string[][] = [];

    campaigns.forEach((campaign) => {
      rows.push([
        "Campaign",
        `"${campaign.campaign_name}"`,
        campaign.total_clicks.toString(),
        campaign.total_conversions.toString(),
        campaign.cvr.toFixed(2),
        campaign.total_revenue != null
          ? campaign.total_revenue.toString()
          : "N/A",
        campaign.revenue_mom != null ? campaign.revenue_mom.toFixed(2) : "N/A",
        campaign.revenue_yoy != null ? campaign.revenue_yoy.toFixed(2) : "N/A",
      ]);

      campaign.ad_groups.forEach((adGroup) => {
        rows.push([
          "Ad Group",
          `"${adGroup.ad_group_name}"`,
          adGroup.total_clicks.toString(),
          adGroup.total_conversions.toString(),
          adGroup.cvr.toFixed(2),
          adGroup.total_revenue != null
            ? adGroup.total_revenue.toString()
            : "N/A",
          adGroup.revenue_mom != null ? adGroup.revenue_mom.toFixed(2) : "N/A",
          adGroup.revenue_yoy != null ? adGroup.revenue_yoy.toFixed(2) : "N/A",
        ]);

        const hasIndividualAds = adGroup.ads.some(
          (ad) => Boolean(ad.ad_id || ad.ad_name),
        );
        if (hasIndividualAds) {
          adGroup.ads.forEach((ad) => {
            rows.push([
              "Ad",
              `"${ad.ad_name || ad.ad_id || "—"}"`,
              ad.clicks.toString(),
              ad.conversions.toString(),
              ad.cvr !== null ? ad.cvr.toFixed(2) : "—",
              ad.revenue != null ? ad.revenue.toString() : "N/A",
              ad.revenue_mom !== null ? ad.revenue_mom.toFixed(2) : "—",
              ad.revenue_yoy !== null ? ad.revenue_yoy.toFixed(2) : "—",
            ]);
          });
        }
      });
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `campaign-ad-performance-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={`rounded-3xl p-4 sm:p-6 border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3
          className={`text-base sm:text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
        >
          {t(titleKey)}
        </h3>
        {showExport && (
          <button
            onClick={exportToCSV}
            className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
              darkMode
                ? "bg-gray-700 hover:bg-gray-600 text-gray-300"
                : "bg-gray-100 hover:bg-gray-200 text-gray-700"
            }`}
            title={t("reports.exportCSV")}
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">{t("reports.exportCSV")}</span>
          </button>
        )}
      </div>

      <div
        className="overflow-x-auto overflow-y-auto -mx-4 sm:mx-0 rounded-2xl border border-gray-100 dark:border-gray-700/60"
        style={maxHeight && maxHeight !== "none" ? { maxHeight } : undefined}
      >
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full w-full">
            <thead className={`sticky top-0 z-10 shadow-sm ${darkMode ? "bg-gray-800" : "bg-white"}`}>
              <tr
                className={`border-b ${darkMode ? "border-gray-700" : "border-gray-200"}`}
              >
                <th
                  className={`sticky top-0 z-10 text-left py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.campaign")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.clicks")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.conversions_col")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.cvr")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.revenue")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.revenueMom")}
                </th>
                <th
                  className={`sticky top-0 z-10 text-right py-3 px-4 text-xs sm:text-sm font-medium whitespace-nowrap border-b ${
                    darkMode
                      ? "text-gray-400 bg-gray-800 border-gray-700"
                      : "text-gray-600 bg-white border-gray-200"
                  }`}
                >
                  {t("reports.revenueYoy")}
                </th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => {
                const isCampaignExpanded = expandedCampaigns.has(
                  campaign.campaign_id,
                );

                return (
                  <Fragment key={campaign.campaign_id}>
                    <tr
                        key={campaign.campaign_id}
                        className={`border-b transition-colors cursor-pointer ${
                          darkMode
                            ? "border-gray-700 hover:bg-gray-700/50"
                            : "border-gray-100 hover:bg-gray-50"
                        }`}
                        onClick={() => toggleCampaign(campaign.campaign_id)}
                      >
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm font-medium flex items-center gap-2 ${darkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {isCampaignExpanded ? (
                            <ChevronDown className="w-4 h-4 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 flex-shrink-0" />
                          )}
                          <span className="truncate">
                            {campaign.campaign_name}
                          </span>
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {formatCompactNumber(campaign.total_clicks)}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {formatCompactNumber(campaign.total_conversions)}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {formatPercentage(campaign.cvr)}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {formatCompactCurrency(campaign.total_revenue)}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                            campaign.revenue_mom != null &&
                            campaign.revenue_mom >= 0
                              ? "text-green-500"
                              : campaign.revenue_mom != null
                                ? "text-red-500"
                                : darkMode
                                  ? "text-gray-400"
                                  : "text-gray-600"
                          }`}
                        >
                          {campaign.revenue_mom != null
                            ? `${campaign.revenue_mom >= 0 ? "+" : ""}${formatPercentage(campaign.revenue_mom)}`
                            : "—"}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                            campaign.revenue_yoy != null &&
                            campaign.revenue_yoy >= 0
                              ? "text-green-500"
                              : campaign.revenue_yoy != null
                                ? "text-red-500"
                                : darkMode
                                  ? "text-gray-400"
                                  : "text-gray-600"
                          }`}
                        >
                          {campaign.revenue_yoy != null
                            ? `${campaign.revenue_yoy >= 0 ? "+" : ""}${formatPercentage(campaign.revenue_yoy)}`
                            : "—"}
                        </td>
                      </tr>

                      {isCampaignExpanded &&
                        campaign.ad_groups.map((adGroup) => {
                          const adGroupKey = getAdGroupKey(
                            campaign.campaign_id,
                            adGroup.ad_group_id,
                          );
                          const hasIndividualAds = adGroup.ads.some(
                            (ad) => Boolean(ad.ad_id || ad.ad_name),
                          );
                          const isAdGroupExpanded =
                            hasIndividualAds && expandedAdGroups.has(adGroupKey);

                          return (
                            <Fragment key={adGroupKey}>
                              <tr
                                key={adGroupKey}
                                className={`border-b transition-colors ${
                                  hasIndividualAds ? "cursor-pointer" : ""
                                } ${
                                  darkMode
                                    ? "border-gray-700/50 bg-gray-700/20 hover:bg-gray-700/35"
                                    : "border-gray-100 bg-gray-50/50 hover:bg-gray-100/70"
                                }`}
                                onClick={() => {
                                  if (hasIndividualAds) {
                                    toggleAdGroup(
                                      campaign.campaign_id,
                                      adGroup.ad_group_id,
                                    );
                                  }
                                }}
                              >
                                <td
                                  className={`py-2 px-4 pl-12 text-xs sm:text-sm font-medium flex items-center gap-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {hasIndividualAds ? (
                                    isAdGroupExpanded ? (
                                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                                    )
                                  ) : (
                                    <span className="w-4 h-4 flex-shrink-0" />
                                  )}
                                  <span className="truncate">
                                    {adGroup.ad_group_name}
                                  </span>
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {formatCompactNumber(adGroup.total_clicks)}
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {formatCompactNumber(
                                    adGroup.total_conversions,
                                  )}
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {formatPercentage(adGroup.cvr)}
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {formatCompactCurrency(adGroup.total_revenue)}
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                                    adGroup.revenue_mom != null &&
                                    adGroup.revenue_mom >= 0
                                      ? "text-green-500"
                                      : adGroup.revenue_mom != null
                                        ? "text-red-500"
                                        : darkMode
                                          ? "text-gray-400"
                                          : "text-gray-600"
                                  }`}
                                >
                                  {adGroup.revenue_mom != null
                                    ? `${adGroup.revenue_mom >= 0 ? "+" : ""}${formatPercentage(adGroup.revenue_mom)}`
                                    : "—"}
                                </td>
                                <td
                                  className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                                    adGroup.revenue_yoy != null &&
                                    adGroup.revenue_yoy >= 0
                                      ? "text-green-500"
                                      : adGroup.revenue_yoy != null
                                        ? "text-red-500"
                                        : darkMode
                                          ? "text-gray-400"
                                          : "text-gray-600"
                                  }`}
                                >
                                  {adGroup.revenue_yoy != null
                                    ? `${adGroup.revenue_yoy >= 0 ? "+" : ""}${formatPercentage(adGroup.revenue_yoy)}`
                                    : "—"}
                                </td>
                              </tr>

                              {hasIndividualAds &&
                                isAdGroupExpanded &&
                                adGroup.ads.map((ad, adIdx) => (
                                  <tr
                                    key={`${adGroupKey}::${ad.ad_id || ad.ad_name || adIdx}`}
                                    className={`border-b transition-colors ${
                                      darkMode
                                        ? "border-gray-700/30 bg-gray-900/10"
                                        : "border-gray-100 bg-white"
                                    }`}
                                  >
                                    <td
                                      className={`py-2 px-4 pl-20 text-xs sm:text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                                    >
                                      <span className="truncate">
                                        {ad.ad_name || "—"}
                                      </span>
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                    >
                                      {formatCompactNumber(ad.clicks)}
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                    >
                                      {formatCompactNumber(ad.conversions)}
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                    >
                                      {ad.cvr !== null
                                        ? formatPercentage(ad.cvr)
                                        : "—"}
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                                    >
                                      {formatCompactCurrency(ad.revenue)}
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                                        ad.revenue_mom !== null &&
                                        ad.revenue_mom >= 0
                                          ? "text-green-500"
                                          : ad.revenue_mom !== null
                                            ? "text-red-500"
                                            : darkMode
                                              ? "text-gray-400"
                                              : "text-gray-600"
                                      }`}
                                    >
                                      {ad.revenue_mom !== null
                                        ? `${ad.revenue_mom >= 0 ? "+" : ""}${formatPercentage(ad.revenue_mom)}`
                                        : "—"}
                                    </td>
                                    <td
                                      className={`py-2 px-4 text-xs sm:text-sm text-right whitespace-nowrap ${
                                        ad.revenue_yoy !== null &&
                                        ad.revenue_yoy >= 0
                                          ? "text-green-500"
                                          : ad.revenue_yoy !== null
                                            ? "text-red-500"
                                            : darkMode
                                              ? "text-gray-400"
                                              : "text-gray-600"
                                      }`}
                                    >
                                      {ad.revenue_yoy !== null
                                        ? `${ad.revenue_yoy >= 0 ? "+" : ""}${formatPercentage(ad.revenue_yoy)}`
                                        : "—"}
                                    </td>
                                  </tr>
                                ))}
                            </Fragment>
                          );
                        })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mt-4">
        <div
          className={`rounded-2xl p-3 sm:p-4 border ${
            darkMode
              ? "bg-gradient-to-br from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
              : "bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
          }`}
        >
          <p
            className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {t("reports.totalImpressions")}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {formatCompactNumber(totals.total_impressions ?? 0)}
          </p>
        </div>

        <div
          className={`rounded-2xl p-3 sm:p-4 border ${
            darkMode
              ? "bg-gradient-to-br from-[var(--brand-secondary)]/20 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/30"
              : "bg-gradient-to-br from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/20"
          }`}
        >
          <p
            className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {t("reports.totalClicks")}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {formatCompactNumber(totals.total_clicks)}
          </p>
        </div>

        <div
          className={`rounded-2xl p-3 sm:p-4 border ${
            darkMode
              ? "bg-gradient-to-br from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
              : "bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
          }`}
        >
          <p
            className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {t("reports.totalConversions")}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {formatCompactNumber(totals.total_conversions)}
          </p>
        </div>

        <div
          className={`rounded-2xl p-3 sm:p-4 border ${
            darkMode
              ? "bg-gradient-to-br from-[var(--brand-secondary)]/20 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/30"
              : "bg-gradient-to-br from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/20"
          }`}
        >
          <p
            className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {t("reports.totalAdSpend") || "Total Ad Spend"}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {formatCompactCurrency(totals.total_cost ?? 0)}
          </p>
        </div>

        <div
          className={`rounded-2xl p-3 sm:p-4 border ${
            darkMode
              ? "bg-gradient-to-br from-[var(--brand-primary)]/20 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/30"
              : "bg-gradient-to-br from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/20"
          }`}
        >
          <p
            className={`text-xs font-medium mb-1 ${darkMode ? "text-gray-300" : "text-gray-600"}`}
          >
            {t("reports.totalRevenue")}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
          >
            {totals.total_revenue && totals.total_revenue > 0
              ? formatCompactCurrency(totals.total_revenue)
              : "N/A"}
          </p>
        </div>
      </div>
    </div>
  );
}
